import {
  CountryCode,
  parsePhoneNumberFromString,
  validatePhoneNumberLength,
} from 'libphonenumber-js';
import validator from 'validator';
import * as yup from 'yup';

type PhoneNumberOptions = {
  allowLocal?: boolean;
  defaultCountry?: CountryCode;
  message?: string;
};

type ParsedPhoneNumber = {
  countryCode: string;
  number: string;
};

const FALLBACK_PHONE_COUNTRY: CountryCode = 'US';

export const getDefaultPhoneCountry = (): CountryCode => {
  if (typeof navigator === 'undefined') {
    return FALLBACK_PHONE_COUNTRY;
  }

  const locale = navigator.languages?.[0] || navigator.language || '';
  const localeParts = locale.split('-').filter(Boolean);
  const localeRegion = localeParts
    .slice(1)
    .reverse()
    .find((part) => /^[A-Za-z]{2}$/.test(part))
    ?.toUpperCase();

  if (/^[A-Z]{2}$/.test(localeRegion || '')) {
    return localeRegion as CountryCode;
  }

  return FALLBACK_PHONE_COUNTRY;
};

export const normalizePhoneNumber = (
  value: string,
  options: Omit<PhoneNumberOptions, 'message'> = {}
): ParsedPhoneNumber | null => {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return null;
  }

  const allowLocal = options.allowLocal ?? false;
  const hasExplicitCountryCode = trimmedValue.startsWith('+');

  if (!hasExplicitCountryCode && !allowLocal) {
    return null;
  }

  const defaultCountry = options.defaultCountry ?? getDefaultPhoneCountry();
  const parsedPhone = hasExplicitCountryCode
    ? parsePhoneNumberFromString(trimmedValue)
    : parsePhoneNumberFromString(trimmedValue, defaultCountry);

  if (!parsedPhone) {
    return null;
  }

  const lengthError = hasExplicitCountryCode
    ? validatePhoneNumberLength(trimmedValue)
    : validatePhoneNumberLength(trimmedValue, defaultCountry);

  if (lengthError) {
    return null;
  }

  return {
    countryCode: `+${parsedPhone.countryCallingCode}`,
    number: parsedPhone.nationalNumber,
  };
};

yup.addMethod<yup.StringSchema>(yup.string, 'stripEmptyString', function transform() {
  return this.transform((value: string) => (value === '' ? undefined : value));
});

yup.addMethod<yup.StringSchema>(yup.string, 'password', function password() {
  return this.test({
    name: 'isPasswordValid',
    message:
      'Password must contain at least 1 uppercase, 1 lowercase, 1 number, 1 special character and minimum 6 characters',
    test: (value) =>
      validator.isStrongPassword(value ?? '', {
        minLength: 6,
        minLowercase: 1,
        minUppercase: 1,
        minNumbers: 1,
        minSymbols: 1,
      }),
  });
});

yup.addMethod<yup.StringSchema>(
  yup.string,
  'email',
  function validateEmail(message = 'Enter a valid email address') {
    return this.matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, message);
  }
);

yup.addMethod<yup.StringSchema>(
  yup.string,
  'phoneNumber',
  function phoneNumber(options: PhoneNumberOptions = {}) {
    const allowLocal = options.allowLocal ?? false;
    const invalidMessage =
      options.message ||
      (allowLocal
        ? 'Please enter a valid phone number, or leave this field blank.'
        : 'Invalid phone number');

    return this.test({
      name: 'isValidPhoneNumber',
      message: invalidMessage,
      test: (value, ctx) => {
        if (!value || !value.trim()) {
          return true;
        }

        const normalizedPhone = normalizePhoneNumber(value, {
          allowLocal,
          defaultCountry: options.defaultCountry,
        });

        if (normalizedPhone) {
          return true;
        }

        if (!allowLocal && !value.trim().startsWith('+')) {
          return ctx.createError({
            message: 'Invalid country code',
            path: ctx.path,
          });
        }

        return ctx.createError({
          message: invalidMessage,
          path: ctx.path,
        });
      },
    });
  }
);
export default yup;
